import React, { useEffect, useMemo } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Language as LanguageIcon } from '@mui/icons-material';
import 'flag-icons/css/flag-icons.min.css';

type Props = {
  onChanged?: (lang: string) => void;
  selectedColor?: string;
};

const normalizeLanguage = (lang: string | undefined | null) => {
  const lng = (lang ?? '').trim();
  if (!lng) return 'en-US';
  if (lng.toLowerCase().startsWith('pt')) return 'pt-BR';
  return 'en-US';
};

const LanguageSelector: React.FC<Props> = ({ onChanged, selectedColor }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng');

    const normalizedSavedLanguage = normalizeLanguage(savedLanguage);
    if (savedLanguage && savedLanguage !== normalizedSavedLanguage) {
      i18n.changeLanguage(normalizedSavedLanguage);
      localStorage.setItem('i18nextLng', normalizedSavedLanguage);
    }
  }, [i18n]);

  const currentLanguage = useMemo(() => normalizeLanguage(i18n.resolvedLanguage ?? i18n.language), [i18n.language, i18n.resolvedLanguage]);

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const selectedLanguage = normalizeLanguage(event.target.value);
    i18n.changeLanguage(selectedLanguage);
    localStorage.setItem('i18nextLng', selectedLanguage);
    if (onChanged) onChanged(selectedLanguage);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LanguageIcon sx={{ color: selectedColor ?? 'inherit', fontSize: '1.2rem' }} />
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <Select
          value={currentLanguage}
          onChange={handleLanguageChange}
          sx={{
            color: selectedColor ?? 'inherit',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiSelect-icon': {
              color: 'inherit',
            },
          }}
          renderValue={(selected) => {
            const code = selected === 'pt-BR' ? 'br' : 'us';
            const label = selected === 'pt-BR' ? 'Português (Brasil)' : 'English (US)';
            return (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span className={`fi fi-${code}`} style={{ marginRight: 8 }} />
                <Typography variant="body2">{label}</Typography>
              </Box>
            );
          }}
        >
          <MenuItem value="pt-BR">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span className="fi fi-br" />
              <Typography variant="body2">Português (Brasil)</Typography>
            </Box>
          </MenuItem>
          <MenuItem value="en-US">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span className="fi fi-us" />
              <Typography variant="body2">English (US)</Typography>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default LanguageSelector;
